import {
  type DoorStyleCard as DoorStyleCardData,
  type DoorStyleFinish,
  doorStyleCode,
} from '@dealerportal/common';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import { useState } from 'react';
import { MissingImage } from '../ProductCard/MissingImage';

/** Swatches shown before the row collapses behind a "+N" button. */
const SWATCH_LIMIT = 7;

export type DoorStyleCardProps = {
  card: DoorStyleCardData;
  /**
   * Detail-page link for the door image, name and Visualize button. A
   * function receives the finish the card shows (its first), so the link
   * can lead to that exact door × finish.
   */
  href?: string | ((finish: DoorStyleFinish | null) => string | undefined);
  /** Renders the Order Sample button; receives the finish the card shows. */
  onOrderSample?: (
    card: DoorStyleCardData,
    finish: DoorStyleFinish | null
  ) => void;
};

/**
 * One door style with its orderable finishes as swatches — the KB&F
 * counterpart of the flooring product-line card, reusing its markup so the
 * catalog styles apply.
 */
export const DoorStyleCard = ({
  card,
  href,
  onOrderSample,
}: DoorStyleCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const swatches = expanded
    ? card.finishes
    : card.finishes.slice(0, SWATCH_LIMIT);
  const hiddenCount = card.finishes.length - swatches.length;
  const current = card.finishes[0] ?? null;
  const link = typeof href === 'function' ? href(current) : href;

  // CiC door renders are wide (~900x756) with baked-in white backgrounds, so
  // they sit centered on a fixed-height white stage; max-w/h keeps their
  // intrinsic width from stretching the grid track (and the card with it).
  const image = (
    <div className="product-card__image door-style-card__stage ui-h-56 md:ui-h-64 ui-p-3 ui-box-border ui-flex ui-items-center ui-justify-center ui-bg-white">
      {card.imageUrl ? (
        <img
          className="ui-max-w-full ui-max-h-full ui-object-contain"
          alt={`${card.name} door style`}
          title={card.name}
          src={card.imageUrl}
          loading="lazy"
        />
      ) : (
        <MissingImage
          className="ui-max-w-full ui-max-h-full ui-fill-gray-400/50"
          height={224}
          width={224}
        />
      )}
    </div>
  );

  return (
    <div
      data-testid={`door-style-card-${card.code}`}
      className="product-card door-style-card ui-min-w-0 ui-flex ui-flex-col ui-justify-between ui-rounded ui-overflow-hidden ui-bg-white"
    >
      {link ? (
        <a href={link} className="ui-block" aria-label={card.name}>
          {image}
        </a>
      ) : (
        image
      )}

      <div className="ui-flex-1 ui-flex ui-flex-col">
        {/* Brand, style, species — the flooring card's brand / line / colour
            rows. No price group: it is a dealer pricing tier. */}
        <div className="ui-p-4 !ui-pb-4 ui-space-y-1">
          {card.brandName && (
            <div
              className="product-card__brand ui-text-sm ui-font-semibold ui-text-gray-700"
              data-testid="door-style-brand"
            >
              {card.brandName}
            </div>
          )}
          <div className="product-card__line !ui-font-semibold !ui-text-gray-900">
            {link ? (
              <a href={link} className="ui-text-inherit ui-no-underline">
                {card.name}
              </a>
            ) : (
              card.name
            )}
            {/* The catalog's own code next to a style name: what dealers
                quote by. */}
            {card.styleName && (
              <span className="product-card__code ui-ml-2 ui-text-sm ui-font-normal ui-text-gray-500">
                {doorStyleCode(card)}
              </span>
            )}
          </div>
          {card.species.length > 0 && (
            <div
              className="product-card__species ui-text-sm ui-text-gray-600"
              data-testid="door-style-species"
            >
              {card.species.join(' · ')}
            </div>
          )}

          {card.finishes.length > 0 && (
            <div className="product-card__swatch_thumbnails ui-flex ui-flex-wrap ui-items-start ui-gap-2 ui-mt-4">
              {/* Same 24px swatch grid as ProductCardSwatches. Display only:
                  CiC has one render per door style, so picking a finish could
                  not change the image. The name shows on hover. */}
              <ul className="ui-grid ui-grid-cols-7 ui-gap-2 ui-list-none ui-p-0 ui-m-0">
                {swatches.map((finish) => (
                  <li
                    key={finish.code}
                    title={finish.name}
                    data-testid="door-style-swatch"
                    className="ui-h-6 ui-w-6 ui-shrink-0 ui-rounded-full ui-overflow-hidden ui-bg-gray-200"
                  >
                    {finish.imageUrl ? (
                      <img
                        src={finish.imageUrl}
                        alt={finish.name}
                        loading="lazy"
                        className="ui-block ui-w-full ui-h-full ui-object-cover"
                      />
                    ) : (
                      <span className="ui-sr-only">{finish.name}</span>
                    )}
                  </li>
                ))}
              </ul>
              {card.finishes.length > SWATCH_LIMIT && (
                <button
                  type="button"
                  onClick={() => setExpanded(!expanded)}
                  aria-label={
                    expanded
                      ? 'Show fewer finishes'
                      : `Show ${hiddenCount} more finishes`
                  }
                  className="ui-flex ui-shrink-0 ui-items-center ui-justify-center ui-h-6 ui-w-6 ui-box-border ui-rounded-full ui-border-0 ui-bg-gray-200 ui-text-xs ui-font-semibold ui-text-gray-700 ui-cursor-pointer"
                >
                  {expanded ? '−' : `+${hiddenCount}`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Same button row as the flooring ProductCard: Order Sample
            (primary) and the visualizer link to the PDP (neutral when both
            are shown). */}
        {(onOrderSample || link) && (
          <div className="ui-flex ui-flex-wrap ui-gap-1 ui-pb-4 ui-pt-1 ui-px-4 ui-mt-auto">
            {onOrderSample && (
              <BetterButton
                btnStyle="primary"
                label="Order Sample"
                replaceClass={{
                  'ui-h-12': 'ui-h-10',
                  '!ui-px-6': 'ui-px-3',
                }}
                attributes={{
                  onClick: () => onOrderSample(card, current),
                }}
              />
            )}
            {link && (
              <div className="product-card__viz-button">
                <BetterButton
                  btnStyle={onOrderSample ? 'neutral' : 'primary'}
                  href={link}
                  label="Visualize"
                  replaceClass={{
                    'ui-h-12': 'ui-h-10',
                    '!ui-px-6': onOrderSample ? 'ui-px-2.5' : 'ui-px-3',
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
